import React from 'react';
import { marked } from 'marked';
import {
  Gavel,
  Target,
  ShieldAlert,
  ScrollText,
  TrendingUp,
  Calendar,
  FileText,
  CheckCircle2,
  AlertCircle,
  Briefcase,
  ExternalLink
} from 'lucide-react';

const CanvasRing = ({ value, size = 100, strokeWidth = 8, color = '#2563eb' }) => {
  const canvasRef = React.useRef(null);
  const [currentValue, setCurrentValue] = React.useState(0);

  React.useEffect(() => {
    const duration = 800;
    const startTime = performance.now();
    const animate = (time) => {
      const progress = Math.min((time - startTime) / duration, 1);
      const ease = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
      setCurrentValue(value * ease);
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [value]);

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    canvas.style.width = `${size}px`;
    canvas.style.height = `${size}px`;
    ctx.scale(dpr, dpr);

    const center = size / 2;
    const radius = (size - strokeWidth) / 2;
    ctx.clearRect(0, 0, size, size);

    // Track
    ctx.beginPath();
    ctx.arc(center, center, radius, 0, 2 * Math.PI);
    ctx.strokeStyle = '#f1f5f9';
    ctx.lineWidth = strokeWidth;
    ctx.stroke();

    // Progress
    if (currentValue > 0) {
      ctx.beginPath();
      ctx.arc(center, center, radius, -Math.PI / 2, (-Math.PI / 2) + (currentValue / 100) * 2 * Math.PI);
      ctx.strokeStyle = color;
      ctx.lineWidth = strokeWidth;
      ctx.lineCap = 'round';
      ctx.stroke();
    }
  }, [currentValue, size, strokeWidth, color]);

  return <canvas ref={canvasRef} />;
};

const AnalysisResult = ({ content }) => {
  if (!content) return null;

  // Extraction Logic (Simplified)
  const winProbability = (content.match(/\[WIN_PROBABILITY\]:\s*(\d+)%/i)?.[1]) || null;
  const strategicSteps = content.match(/\[STRATEGIC_STEPS\]:\s*(.*)/i)?.[1]?.split(',').map(s => s.trim()) || [];

  const caseMeta = {
    name: content.match(/\*\*Case:\*\*\s*(.*)/i)?.[1],
    number: content.match(/\*\*Case Number:\*\*\s*(.*)/i)?.[1],
    status: content.match(/\*\*Status:\*\*\s*(.*)/i)?.[1],
    type: content.match(/\*\*Type:\*\*\s*(.*)/i)?.[1],
    next: content.match(/\*\*Next Hearing:\*\*\s*(.*)/i)?.[1]
  };

  const isSpecializedResult = winProbability !== null || caseMeta.name || caseMeta.number;

  const renderMarkdown = (text) => ({ __html: marked(text || '') });

  if (!isSpecializedResult) {
    return (
      <div className="simple-msg-card">
        <div className="markdown-content" dangerouslySetInnerHTML={renderMarkdown(content)} />
        <style jsx>{`
          .simple-msg-card {
            background: #ffffff;
            border-radius: 12px;
            padding: 16px 20px;
            border: 1px solid #f1f5f9;
            color: #334155;
            font-size: 13.5px;
            line-height: 1.6;
            box-shadow: 0 1px 2px rgba(0,0,0,0.03);
          }
        `}</style>
      </div>
    );
  }

  const cleanContent = content
    .replace(/\[WIN_PROBABILITY\]:.*$/m, '')
    .replace(/\[STRATEGIC_STEPS\]:.*$/m, '')
    .trim();

  const sections = cleanContent.split(/\n---\n|\n---$/);
  const mainAnalysis = sections.length > 1 ? sections.slice(1).join('\n---\n') : cleanContent;

  return (
    <div className="predict-dashboard-container">

      <div className="dashboard-grid">
        {/* Metric Card */}
        <div className="panel metric-panel">
          <div className="panel-header-mini">
            <Target size={14} /> <span>Confidence Score</span>
          </div>
          <div className="metric-body">
            <div className="ring-wrapper">
              <CanvasRing value={winProbability || 0} size={80} strokeWidth={6} />
              <div className="ring-content">
                <span className="value">{winProbability}%</span>
                <span className="label">WIN PROB</span>
              </div>
            </div>
            <div className="metric-insight">
              <div className="insight-row">
                <div className="insight-dot pros"></div>
                <span>Prosecution Range</span>
                <span className="val">~75%</span>
              </div>
              <div className="insight-row">
                <div className="insight-dot def"></div>
                <span>Defense Mitigation</span>
                <span className="val">~25%</span>
              </div>
            </div>
          </div>
        </div>

        {/* Guidance Card */}
        <div className="panel guidance-panel">
          <div className="panel-header-mini">
            <TrendingUp size={14} /> <span>Strategic Guidance</span>
          </div>
          <div className="steps-compact-list">
            {strategicSteps.map((step, i) => (
              <div key={i} className="step-row-mini">
                <div className="step-bullet"><CheckCircle2 size={12} /></div>
                <p>{step}</p>
              </div>
            ))}
            {strategicSteps.length === 0 && <span className="empty-text">No active recommendations.</span>}
          </div>
        </div>

        {/* Detailed Insights */}
        <div className="panel content-panel full-bleed">
          <div className="panel-header-mini">
            <ScrollText size={14} /> <span>Detailed Analysis & Reasoning</span>
          </div>
          <div className="prose-container" dangerouslySetInnerHTML={renderMarkdown(mainAnalysis)} />
        </div>
      </div>

      <style jsx>{`

        .predict-dashboard-container {
          display: flex;
          flex-direction: column;
          gap: 12px;
          width: 100%;
          font-family: 'Inter', system-ui, sans-serif;
          color: #0f172a;
        }

        .panel {
          background: #ffffff;
          border: 1px solid #f1f5f9;
          border-radius: 10px;
          padding: 16px;
          box-shadow: 0 1px 2px rgba(0,0,0,0.02);
        }

        .header-panel {
          border-left: 3px solid #2563eb;
        }

        .panel-top {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 8px;
        }

        .badge-group {
          display: flex;
          gap: 8px;
          align-items: center;
        }

        .badge-legal {
          font-size: 9px;
          font-weight: 700;
          color: #64748b;
          letter-spacing: 0.05em;
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .badge-status {
          font-size: 10px;
          font-weight: 600;
          padding: 2px 8px;
          border-radius: 4px;
          background: #f1f5f9;
          color: #475569;
        }

        .badge-status.ongoing {
          background: #eff6ff;
          color: #2563eb;
        }

        .display-title {
          font-size: 16px;
          font-weight: 700;
          margin: 0 0 16px 0;
          letter-spacing: -0.01em;
        }

        .meta-compact-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
          gap: 16px;
          padding-top: 12px;
          border-top: 1px solid #f8fafc;
        }

        .meta-cell {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .meta-cell label {
          font-size: 8px;
          font-weight: 700;
          color: #94a3b8;
          margin: 0;
        }

        .meta-cell span {
          font-size: 11px;
          font-weight: 600;
          color: #475569;
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .dashboard-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
        }

        .full-bleed {
          grid-column: span 2;
        }

        .panel-header-mini {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 11px;
          font-weight: 700;
          color: #64748b;
          text-transform: uppercase;
          letter-spacing: 0.02em;
          margin-bottom: 16px;
        }

        .metric-body {
          display: flex;
          flex-direction: column; /* Large/Default: Top to Bottom */
          align-items: center;
          justify-content: center;
          gap: 16px;
          padding: 8px 0;
          width: 100%;
          height:80%;
        }

        .ring-wrapper {
          position: relative;
          width: 80px;
          height: 80px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .ring-content {
          position: absolute;
          display: flex;
          flex-direction: column;
          align-items: center;
          line-height: 1;
        }

        .ring-content .value {
          font-size: 18px;
          font-weight: 800;
          color: #0f172a;
        }

        .ring-content .label {
          font-size: 7px;
          font-weight: 800;
          color: #94a3b8;
          margin-top: 2px;
        }

        .metric-insight {
          width: 100%;
          display: flex;
          flex-direction: column;
          gap: 6px;
          max-width: 240px;
        }

        .insight-row {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 10px;
          color: #64748b;
          font-weight: 500;
          padding: 4px 8px;
          background: #f8fafc;
          border-radius: 4px;
        }

        .insight-row .val {
          margin-left: auto;
          font-weight: 700;
          color: #334155;
        }

        .insight-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
        }

        .insight-dot.pros { background: #2563eb; }
        .insight-dot.def { background: #e2e8f0; }

        .steps-compact-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .step-row-mini {
          display: flex;
          gap: 10px;
          padding: 8px 12px;
          background: #f8fafc;
          border-radius: 6px;
          border: 1px solid #f1f5f9;
        }

        .step-bullet {
          color: #2563eb;
          flex-shrink: 0;
          margin-top: 1px;
        }

        .step-row-mini p {
          font-size: 10.5px;
          font-weight: 500;
          line-height: 1.4;
          color: #334155;
          margin: 0;
        }

        .prose-container {
          font-size: 12.5px;
          line-height: 1.6;
          color: #475569;
        }

        .prose-container :global(h2) {
          font-size: 13px;
          font-weight: 700;
          color: #0f172a;
          margin: 16px 0 8px 0;

        .inline-icon {
          margin-right: 4px;
          vertical-align: middle;
        }

        @media (max-width: 768px) {
          .dashboard-grid {
            grid-template-columns: 1fr;
          }
          .full-bleed {
            grid-column: span 1;
          }
          .metric-body {
            flex-direction: row; /* Small: Side by Side */
            justify-content: space-evenly;
            padding: 12px 0;
          }
          .metric-insight {
            max-width: 160px;
          }
        }
      `}</style>
    </div>
  );
};

export default AnalysisResult;
